import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, inject, OnInit, signal, viewChild } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { BiTranslatePipe, BiTranslateService } from "@globals/bi-translate";
import { CustomAnimations } from "@globals/classes/CustomAnimations";
import { IMapLegendItem } from "@globals/components/bi-map/bi-map-legend/IMapLegendItem";
import { BiMapComponent } from "@globals/components/bi-map/bi-map.component";
import { BiMapService } from "@globals/components/bi-map/bi-map.service";
import { BiMapMarker } from "@globals/components/bi-map/BiMapMarker";
import { BiNotificationBarComponent } from "@globals/components/bi-notification-bar/bi-notification-bar.component";
import { BiSpinnerComponent } from "@globals/components/spinner/bi-spinner.component";
import { ColorEnum, ConverStringToColorEnum } from "@globals/enums/ColorEnum";
import { cloneObject, getLanguageCodeByCountryId } from "@globals/helper-functions";
import { BiHttpErrorResponse } from "@globals/models/BiHttpErrorResponse";
import { GeoJson } from "@globals/models/geography/GeoJson";
import { DriftstatusWebMessagesMapModel } from "@globals/openapi-models/model/driftstatusWebMessagesMapModel";
import { LatLngModel } from "@globals/openapi-models/model/latLngModel";
import { ProfileMapSettingsAndMessages } from "@globals/openapi-models/model/profileMapSettingsAndMessages";
import { WebMessageMapReadModel } from "@globals/openapi-models/model/webMessageMapReadModel";
import { BiDomService } from "@globals/services/bi-dom.service";
import { BiNotificationConfig, BiToastNotificationService, NotificationType } from "@globals/services/bi-toast-notification.service";
import { BiWebMessageBaseService } from "@globals/services/bi-web-messages-base.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import L, { Circle, FeatureGroup, geoJSON, latLng, LatLng, LatLngBounds, marker, PopupOptions } from "leaflet";
import { filter, finalize, switchMap, take, throwError } from "rxjs";
import { WebModuleService } from "../core/web-module.service";
import { IframeDriftstatusMapUrlParams } from "./IframeDriftstatusMapUrlParams";

@UntilDestroy()
@Component({
  selector: "driftstatus-map",
  imports: [BiMapComponent, BiNotificationBarComponent, BiSpinnerComponent, BiTranslatePipe],
  templateUrl: "./iframe-driftstatus-map.component.html",
  styleUrls: ["./iframe-driftstatus-map.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [CustomAnimations.fadeIn],
  host: {
    class: "relative block"
  },
  providers: [WebModuleService, BiDomService, BiWebMessageBaseService, BiMapService, BiToastNotificationService]
})
export default class IframeDriftstatusMapComponent implements OnInit {
  public mapModuleModel: DriftstatusWebMessagesMapModel;
  private layerGroupForCircleMarkersOrPolygons: FeatureGroup;

  public urlParams: IframeDriftstatusMapUrlParams;

  public selectedWebMessage: WebMessageMapReadModel;

  public popupMessageIsOpen = false;
  public loading = false;

  protected readonly noMessages = signal(false);

  /**
   * List of profile ids controlling which profiles have been activated for display
   * Undefined mean all the profile messages need to be displayed.
   * Controlled using the legend feature.
   */
  private enabledProfileIds: Array<number>;

  private minZoomForMapContent = 14;

  /**
   * A mapping of web message IDs to a list of Leaflet Layer info  (the layers that are drawn for the specific web message).
   * "layerId" is the internal Leaflet Layer ID.
   * "markerCoordinates" is only used if the layer is a single marker.
   */
  private drawnWebMessageIdToLayerInfoMappings: { [webMessageId: number]: Array<{ layerId: number; markerCoordinates?: LatLng }> } = {};

  mapComponent = viewChild<BiMapComponent>("map");

  statusBar = viewChild<ElementRef<HTMLDivElement>>("statusBarWrapper");

  @HostBinding("@fadeIn") public get fadeInHost() {
    return true;
  }

  private readonly notifier = inject(BiToastNotificationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly webMessageModuleService = inject(WebModuleService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly translator = inject(BiTranslateService);
  private readonly mapService = inject(BiMapService);

  constructor() {
    const domService = inject(BiDomService);
    domService.loadStyle("leaflet.css");
    domService.loadStyle("ngx-toastr.css");

    toObservable(this.noMessages)
      .pipe(
        untilDestroyed(this),
        filter(x => x != undefined),
        take(1)
      )
      .subscribe(_ => {
        setTimeout(() => {
          // Set the map height here but first change it
          if (!this.urlParams.height) this.urlParams.height = "900px";
          // Subtract the height of the statusbar so we're not messing with the size of the whole component
          if (this.statusBar()) this.urlParams.height = `calc(${this.urlParams.height} - ${this.statusBar().nativeElement.getBoundingClientRect().height}px)`;

          this.cd.detectChanges();
        }, 200);
      });
  }

  public async ngOnInit() {
    // We need global access to jQuery because of Leaflet so load this here
    const jquery = await import("jquery/dist/jquery.min.js");
    (window as any).$ = jquery.default;

    // Get route params, validate and then get the model for this map module
    this.route.queryParams
      .pipe(
        untilDestroyed(this),
        take(1),
        switchMap((params: IframeDriftstatusMapUrlParams) => {
          this.urlParams = params;

          this.correctUrlColorParams();

          if (!params.customerIds && !params.customerId) return throwError(() => new Error());
          else {
            return this.webMessageModuleService
              .getDriftstatusWebMessagesMapModel(params.customerIds ?? params.customerId, params.internalOnly === 1, this.urlParams, params.profileIds)
              .pipe(untilDestroyed(this));
          }
        })
      )
      .subscribe({
        next: model => {
          this.mapModuleModel = model;
          this.noMessages.set(!model.profileSettingsAndMessages.some(x => x.webMessages.length > 0));
          this.translator.use(getLanguageCodeByCountryId(model.countryId));
          this.cd.markForCheck();
        },
        error: err => {
          if (err instanceof HttpErrorResponse) this.handleServerError(err as BiHttpErrorResponse);
          else {
            const notificationConfig = new BiNotificationConfig(NotificationType.ERROR, this.translator.instant("iframes.InvalidUrl"));
            notificationConfig.sticky = true;
            this.notifier.createNotification(notificationConfig);
          }
        }
      });
    this.processUrlParams();
  }

  /**
   * Makes sure that all URL-params for controlling color has a prepending "#" if they have a value.
   * Typically, these params are set without a "#" and therefor wouldn't work
   */
  private correctUrlColorParams() {
    const urlParamsClone = cloneObject(this.urlParams);

    urlParamsClone.referrer = window?.document?.referrer;
    urlParamsClone.popupHeaderFontColor = this.ensureHashtagPrefix(urlParamsClone.popupHeaderFontColor);
    urlParamsClone.popupBodyFontColor = this.ensureHashtagPrefix(urlParamsClone.popupBodyFontColor);
    urlParamsClone.noMessagesFontColor = this.ensureHashtagPrefix(urlParamsClone.noMessagesFontColor);
    urlParamsClone.noMessagesBackgroundColor = this.ensureHashtagPrefix(urlParamsClone.noMessagesBackgroundColor);

    this.urlParams = urlParamsClone;
  }

  private ensureHashtagPrefix(hex?: string): string {
    if (hex && hex.indexOf("#") === -1) return "#" + hex;
    return hex;
  }

  public onMapReady(_map: L.Map) {
    this.layerGroupForCircleMarkersOrPolygons = new FeatureGroup().addTo(this.mapComponent().map);
    this.checkForLegendActivation();

    // As this method is called before the whole MapComponent is ready, we use timeout
    setTimeout(() => {
      this.setupMap();
      this.processWebMessages();
      this.updatePolygonsCirclesAndMarkersOnMap();
      this.setupMapZoomAndMoveListeners();
      this.setupPopupEvents();
    }, 0);
  }

  /**
   * Add map data based on the associated addresses for each single profile's web messages
   */
  private updatePolygonsCirclesAndMarkersOnMap() {
    if (this.mapComponent().map.getZoom() >= this.minZoomForMapContent && !this.popupMessageIsOpen) {
      this.mapModuleModel.profileSettingsAndMessages.forEach(profileSettingsAndMessage => {
        if (this.enabledProfileIds == undefined || this.enabledProfileIds.findIndex(id => id == profileSettingsAndMessage.id) != -1) {
          profileSettingsAndMessage.webMessages.forEach(webMessage => {
            this.addMapDataForWebMessage(webMessage, profileSettingsAndMessage.cssColor as ColorEnum);
          });
        }
      });
    }
  }

  private processUrlParams() {
    if (this.urlParams.fontUrl) $("head").append(`<link href='${this.urlParams.fontUrl}' rel='stylesheet' type='text/css'>`);
  }

  private setupMap() {
    this.setupMapLatLngAndZoom();

    if (this.mapModuleModel.customerSettings.showArchiveDays > 0)
      this.mapComponent().setupShowFromArchiveToggle(this.mapModuleModel.customerSettings.showArchiveDays, showFromArchive => this.onShowFromArchiveToggled(showFromArchive));
  }

  public setupMapLatLngAndZoom = () => {
    const leafletMap = this.mapComponent().map;
    // First set zoom and coordinates
    const zoom = this.mapModuleModel.customerSettings.mapZoomLevel ? this.mapModuleModel.customerSettings.mapZoomLevel / 10 : 8;

    if (this.mapModuleModel.customerSettings.defaultMapCenterCoords) {
      const latLong = this.mapModuleModel.customerSettings.defaultMapCenterCoords;
      leafletMap.setView(latLng(latLong.lat, latLong.lng), zoom);
    } else leafletMap.setZoom(zoom);
  };

  /**
   * Processes the profile web messages along with the profile's individual settings (color and icon).
   * @param onlyForProfileIds Array of profile IDs. If provided, ONLY webmessages from these profiles should be processed and displayed.
   */
  private processWebMessages(onlyForProfileIds?: Array<number>) {
    if (this.mapModuleModel.profileSettingsAndMessages?.length > 0) {
      // Clear any polygons or makers added earlier
      this.layerGroupForCircleMarkersOrPolygons.clearLayers();

      // Filter on profile ids if necessary
      let profileSettingsAndMessages: Array<ProfileMapSettingsAndMessages>;
      if (onlyForProfileIds) {
        // Clear markers and filter away those profile settings and maessages we do not want.
        this.mapComponent().removeMapMarkers();
        profileSettingsAndMessages = this.mapModuleModel.profileSettingsAndMessages.filter(psam => onlyForProfileIds.indexOf(psam.id) > -1);
      } else profileSettingsAndMessages = this.mapModuleModel.profileSettingsAndMessages;

      // Plot the web messages in on map and use the settings for each individual profile
      const pinMarkers: Array<BiMapMarker> = []; // stores all the main pin markers on map. Used for checking if any coordinates conflict
      profileSettingsAndMessages.forEach(settingsAndMessages => {
        settingsAndMessages.webMessages.forEach(messageAndCoordinates => {
          const layerGroupForFindingCenter = new FeatureGroup(); // for holding all relevant coordinates. Not added to map, we just use it for making it easy to get bounds

          // Affected Addresses
          if (messageAndCoordinates.affectedAddressesCoordinates?.length > 0) {
            // Create CoordSet.
            messageAndCoordinates.affectedAddressesCoordinates.forEach(latLong => {
              layerGroupForFindingCenter.addLayer(L.marker(new LatLng(latLong.lat, latLong.lng)));
            });

            const center = layerGroupForFindingCenter.getBounds().getCenter();
            const mainPinCoordinates: LatLngModel = {
              lat: center.lat,
              lng: center.lng
            };

            // Do we already have a marker pin at this exact location? Then push it a bit
            if (pinMarkers.findIndex(pin => pin.getLatLng().equals(center)) !== -1) mainPinCoordinates.lng += 0.001;

            let clickCallback: () => void;
            let hovercallback: () => void;

            // Define the callback to be used when either hovering or clicking the main marker
            const showInfoCallback = () => {
              this.onMarkerClickedOrHovered(
                messageAndCoordinates,
                ConverStringToColorEnum(settingsAndMessages.cssColor),
                this.mapModuleModel.customerSettings.showInfoAndZoomOnClick,
                messageAndCoordinates.affectedAddressesCoordinates.length > 1 ? undefined : mainPinCoordinates,
                messageAndCoordinates.affectedAddressesCoordinates.length > 1 ? layerGroupForFindingCenter.getBounds() : undefined
              );
            };

            if (this.mapModuleModel.customerSettings.showInfoAndZoomOnClick) clickCallback = showInfoCallback;
            else {
              hovercallback = showInfoCallback;
              clickCallback = () =>
                this.zoomMap(
                  messageAndCoordinates.affectedAddressesCoordinates.length === 1 ? mainPinCoordinates : undefined,
                  messageAndCoordinates.affectedAddressesCoordinates.length > 1 ? layerGroupForFindingCenter.getBounds() : undefined
                );
            }

            // Finally, add the center/main marker for this message/SmsGroup
            const marker = this.mapComponent().createMarkerWithIconUrl(
              latLng(mainPinCoordinates.lat, mainPinCoordinates.lng),
              settingsAndMessages.mapIconUrl,
              this.generatePopupMarkup(messageAndCoordinates.title, messageAndCoordinates.text, messageAndCoordinates.criticalStatus),
              this.generatePopupSettings(),
              clickCallback,
              hovercallback
            );
            pinMarkers.push(marker);
          }
          // From GeoJson Object
          else if (messageAndCoordinates.smsGroupGeoObjects) {
            // Get Coordinates from GeoJsonObjects Instead
            const geoJsonObjects = JSON.parse(messageAndCoordinates.smsGroupGeoObjects) as Array<GeoJson>;

            let mapLayerCoordinates: Array<LatLng> = [];

            geoJsonObjects.forEach(geoJson => {
              // Depending on the type of layer, the geometry coordinates is an array of arrays or just a single coordinate
              if (Array.isArray(geoJson.geometry.coordinates[0])) {
                geoJson.geometry.coordinates.forEach(layerCoordinates => mapLayerCoordinates.push(...layerCoordinates.map((coordSet: Array<number>) => new LatLng(coordSet[1], coordSet[0]))));
              } else mapLayerCoordinates = [new LatLng(geoJson.geometry.coordinates[1] as any, geoJson.geometry.coordinates[0] as any)];
            });

            if (mapLayerCoordinates.length > 1) {
              // Now fill up the layergroup with all the coordinates
              mapLayerCoordinates.forEach((latLong: LatLng) => layerGroupForFindingCenter.addLayer(marker(latLong)));
            }

            const center = mapLayerCoordinates.length > 1 ? layerGroupForFindingCenter.getBounds().getCenter() : mapLayerCoordinates[0];
            const mainPinCoordinates: LatLngModel = {
              lat: center.lat,
              lng: center.lng
            };

            let clickCallback: () => void;
            let hovercallback: () => void;

            // Define the callback to be used when either hovering or clicking the main marker
            const showInfoCallback = () =>
              this.onMarkerClickedOrHovered(
                messageAndCoordinates,
                ConverStringToColorEnum(settingsAndMessages.cssColor),
                this.mapModuleModel.customerSettings.showInfoAndZoomOnClick,
                mapLayerCoordinates.length > 1 ? undefined : mainPinCoordinates,
                mapLayerCoordinates.length > 1 ? layerGroupForFindingCenter.getBounds() : undefined
              );

            if (this.mapModuleModel.customerSettings.showInfoAndZoomOnClick) clickCallback = showInfoCallback;
            else {
              hovercallback = showInfoCallback;
              clickCallback = () => {
                this.selectedWebMessage = messageAndCoordinates;
                this.zoomMap(mapLayerCoordinates.length === 1 ? mainPinCoordinates : undefined, mapLayerCoordinates.length > 1 ? layerGroupForFindingCenter.getBounds() : undefined);
              };
            }

            // Finally, add the center/main marker for this message/SmsGroup
            pinMarkers.push(
              this.mapComponent().createMarkerWithIconUrl(
                latLng(mainPinCoordinates.lat, mainPinCoordinates.lng),
                settingsAndMessages.mapIconUrl,
                this.generatePopupMarkup(messageAndCoordinates.title, messageAndCoordinates.text),
                this.generatePopupSettings(),
                clickCallback,
                hovercallback
              )
            );
          }
        });
      });

      if (pinMarkers.length > 0) {
        if (this.urlParams.showClusters) this.mapComponent().createMarkerClusterForMarkers(pinMarkers);
        else {
          this.mapComponent().addMarkers(pinMarkers);
        }
      }
    }
  }

  private generatePopupMarkup(title: string, text: string, isCriticalMessage = false): string {
    // First the title:
    const popupTitle$ = $("<h3>").append(title);
    if (isCriticalMessage) popupTitle$.append(`<span class="fa-solid fa-exclamation-triangle critical-icon ml-2 inline-block"></span>`);

    if (this.urlParams.popupHeaderFont) popupTitle$.css("font-family", this.urlParams.popupHeaderFont);

    if (this.urlParams.popupHeaderFontSize) popupTitle$.css("font-size", this.urlParams.popupHeaderFontSize + "px");

    // Now the text itself:
    const popupBody$ = $("<div>").append(text);
    if (this.urlParams.popupBodyFont) {
      popupBody$.css("font-family", this.urlParams.popupBodyFont).children().css("font-family", this.urlParams.popupBodyFont); // set the styling for every child!
    }
    if (this.urlParams.popupBodyFontSize) {
      popupBody$
        .css("font-size", this.urlParams.popupBodyFontSize + "px")
        .children()
        .css("font-size", this.urlParams.popupBodyFontSize + "px"); // set the styling for every child!
    }

    if (this.urlParams.popupHeaderFontColor) {
      popupTitle$.css("color", this.urlParams.popupHeaderFontColor);
    }

    if (this.urlParams.popupBodyFontColor) {
      popupBody$.css("color", this.urlParams.popupBodyFontColor).children().css("color", this.urlParams.popupBodyFontColor); // set the styling for every child!
    }

    // Construct popup markup
    return $("<div>").append(popupTitle$).append(popupBody$).html();
  }

  private generatePopupSettings(): PopupOptions {
    return <PopupOptions>{
      maxHeight: this.urlParams.popupHeight,
      minWidth: this.urlParams.popupWidth,
      maxWidth: this.urlParams.popupWidth
    };
  }

  private checkForLegendActivation() {
    if (this.mapModuleModel.customerSettings.showLegend && !this.urlParams.hideLegend) {
      const profileSettingsAndMessages = this.mapModuleModel.profileSettingsAndMessages;

      this.sortLegends(profileSettingsAndMessages);

      const legendItems = profileSettingsAndMessages.map(psam => {
        return <IMapLegendItem>{
          enabled: true,
          header: psam.profileTitle,
          iconUrl: psam.mapIconUrl,
          mapMarkerGroupIdentifierKey: psam.id
        };
      });
      this.mapComponent().setupLegend(legendItems, ids => this.onLegendSelectionChanged(ids));
    }
  }

  public sortLegends(profiles: Array<ProfileMapSettingsAndMessages>): Array<ProfileMapSettingsAndMessages> {
    return profiles.sort((a, b) => {
      if (a.sortOrder != null && b.sortOrder != null) {
        return a.sortOrder - b.sortOrder;
      } else if (a.sortOrder != null) {
        return -1;
      } else if (b.sortOrder != null) {
        return 1;
      } else {
        return a.profileTitle.localeCompare(b.profileTitle);
      }
    });
  }

  private onLegendSelectionChanged(enabledProfileIds: Array<number>) {
    this.enabledProfileIds = enabledProfileIds;
    this.processWebMessages(enabledProfileIds);
    this.mapComponent().map.fireEvent("zoomend");
  }

  private onMarkerClickedOrHovered(webMessageMap: WebMessageMapReadModel, markerColor: ColorEnum, isClicked?: boolean, markerCoord?: LatLngModel, addressAreaBounds?: LatLngBounds) {
    this.selectedWebMessage = webMessageMap;
    if (this.layerGroupForCircleMarkersOrPolygons.getLayers().length > 0) this.clearMapAddressLayers();

    this.addMapDataForWebMessage(webMessageMap, markerColor);

    // Zoom Move to a separate method.
    if (isClicked) this.zoomMap(markerCoord, addressAreaBounds);
  }

  private onShowFromArchiveToggled(showFromArchive: boolean) {
    this.loading = true;
    this.cd.detectChanges();
    this.webMessageModuleService
      .getProfileMapSettingsAndMessages(this.urlParams.customerIds ?? this.urlParams.customerId, this.urlParams.profileIds, showFromArchive, this.urlParams.internalOnly === 1)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cd.detectChanges();
        })
      )
      .subscribe(settingsAndMessages => {
        // Clear map data and process new data
        this.mapModuleModel.profileSettingsAndMessages = settingsAndMessages;
        this.layerGroupForCircleMarkersOrPolygons.clearLayers();
        this.drawnWebMessageIdToLayerInfoMappings = {};
        this.mapComponent().removeMapMarkers();
        this.processWebMessages(this.enabledProfileIds);
      });
  }

  private zoomMap(coord?: LatLngModel, bounds?: LatLngBounds) {
    // Sometimes, we don't have any addresses but only a polygon or Point so take that into account.
    if (coord) this.mapComponent().map.flyTo({ lat: coord.lat, lng: coord.lng }, this.minZoomForMapContent + 2, { animate: true });
    else if (bounds) this.mapComponent().map.flyToBounds(bounds, { animate: true });
  }

  private setupMapZoomAndMoveListeners() {
    let previousZoomLevel = this.mapComponent().map.getZoom();

    this.mapComponent().map.on("zoom", () => {
      // get the current recursevly on map zoom
      const currentZoomLevel = this.mapComponent().map.getZoom();

      // If we're zooming out, clear the polygons or dots and close popup
      if (currentZoomLevel < this.minZoomForMapContent && previousZoomLevel > currentZoomLevel) this.clearMapAddressLayers();

      // Update previous to current.
      previousZoomLevel = currentZoomLevel;
    });

    this.mapComponent().map.addEventListener("moveend", () => this.updatePolygonsCirclesAndMarkersOnMap());
  }

  private setupPopupEvents() {
    this.mapComponent().map.on("popupclose", () => (this.popupMessageIsOpen = false));
    this.mapComponent().map.on("popupopen", () => (this.popupMessageIsOpen = true));
  }

  private addMapDataForWebMessage(webMessage: WebMessageMapReadModel, cssColor: ColorEnum) {
    // This web message could already have its addresses inserted on map but probably not all of them if viewport wasn' big enough. Therefore,
    // check if all addresses are inserted first and react accordingly.

    if (this.drawnWebMessageIdToLayerInfoMappings[webMessage.id] && this.drawnWebMessageIdToLayerInfoMappings[webMessage.id].length === webMessage.affectedAddressesCoordinates.length) {
      return;
    }

    if (webMessage.smsGroupGeoObjects != null && (this.mapModuleModel.customerSettings.showPolygons || this.mapModuleModel.customerSettings.useAreaCirclesInsteadOfSingleAddressMarkers)) {
      this.drawnWebMessageIdToLayerInfoMappings[webMessage.id]?.forEach(layerInfo => this.layerGroupForCircleMarkersOrPolygons.removeLayer(layerInfo.layerId));

      const geos = geoJSON(JSON.parse(webMessage.smsGroupGeoObjects));
      // If the current map view doesn't intersect with any of the polygons, don't do anything
      if (this.mapComponent().map.getBounds().intersects(geos.getBounds())) {
        const layers = this.mapComponent().createLayersFromGeoJson(webMessage.smsGroupGeoObjects) as Array<L.Polygon | L.Circle | L.Polyline>;

        this.drawnWebMessageIdToLayerInfoMappings[webMessage.id] = [];
        layers.forEach(layer => {
          layer.options.color = ConverStringToColorEnum(cssColor);
          layer.options.fillColor = ConverStringToColorEnum(cssColor);

          this.layerGroupForCircleMarkersOrPolygons.addLayer(layer);
          this.drawnWebMessageIdToLayerInfoMappings[webMessage.id].push({ layerId: this.layerGroupForCircleMarkersOrPolygons.getLayerId(layer) });
        });
      }
    } else if ((!this.mapModuleModel.customerSettings.showPolygons || !webMessage.smsGroupGeoObjects) && webMessage.affectedAddressesCoordinates?.length > 0) {
      // Show either markers for each single address or display area circle covering the area of all the addresses
      if (!this.mapModuleModel.customerSettings.useAreaCirclesInsteadOfSingleAddressMarkers || webMessage.affectedAddressesCoordinates.length === 1) {
        webMessage.affectedAddressesCoordinates.forEach(affectedCoordinate => {
          // Is this coordinate already inserted?
          if (
            this.drawnWebMessageIdToLayerInfoMappings[webMessage.id]?.some(
              layerInfo => layerInfo.markerCoordinates.lat === affectedCoordinate.lat && layerInfo.markerCoordinates.lng === affectedCoordinate.lng
            )
          )
            return;

          // If the address isn't in the current map view, do not add it to map!
          if (
            this.mapComponent()
              .map.getBounds()
              .contains(affectedCoordinate as any)
          ) {
            if (!this.drawnWebMessageIdToLayerInfoMappings[webMessage.id]) this.drawnWebMessageIdToLayerInfoMappings[webMessage.id] = [];
            const layer = this.mapService.createCircleMarker(latLng(affectedCoordinate.lat, affectedCoordinate.lng), ConverStringToColorEnum(cssColor), "address-div-icon-drift");
            this.layerGroupForCircleMarkersOrPolygons.addLayer(layer);
            this.drawnWebMessageIdToLayerInfoMappings[webMessage.id].push({ layerId: this.layerGroupForCircleMarkersOrPolygons.getLayerId(layer), markerCoordinates: layer.getLatLng() });
          }
        });
      }
    }
  }

  private handleServerError(err: BiHttpErrorResponse) {
    // if not allowed, show to user by navigating to the unavailable page!
    if (err.status === 403 && err.error?.errorMessage.indexOf("customer") > -1)
      this.router.navigate(["unavailable"], {
        state: { message: this.translator.instant("iFrame.ModuleNotActive") }
      });
  }

  /**
   * Clears all layers on the map and closes any popup.
   * NOTE: IF there's a selected web message (user clicked a message) and an area circle has been drawn for that message, the popup and circle will NOT be removed.
   */
  private clearMapAddressLayers() {
    // Just clear all if there's no selected message OR if not using area circles (if so, we wanna keep the drawn circle if there is any)
    if (!this.selectedWebMessage || !this.mapModuleModel.customerSettings.useAreaCirclesInsteadOfSingleAddressMarkers) {
      this.layerGroupForCircleMarkersOrPolygons.clearLayers();
      this.drawnWebMessageIdToLayerInfoMappings = {};
    } else {
      // Make sure not to remove the circle for the selected web message (if there is any)
      Object.keys(this.drawnWebMessageIdToLayerInfoMappings).forEach(webMessageId => {
        if (
          this.selectedWebMessage.id === +webMessageId &&
          this.drawnWebMessageIdToLayerInfoMappings[webMessageId].length === 1 &&
          (this.layerGroupForCircleMarkersOrPolygons.getLayer(this.drawnWebMessageIdToLayerInfoMappings[webMessageId][0].layerId) as Circle).options?.radius
        ) {
          return;
        } else {
          this.drawnWebMessageIdToLayerInfoMappings[webMessageId].forEach(layerInfo => {
            this.layerGroupForCircleMarkersOrPolygons.removeLayer(layerInfo.layerId);
          });
          delete this.drawnWebMessageIdToLayerInfoMappings[webMessageId];
        }
      });
    }
  }
}
